import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { ColumnGroup } from 'primereact/columngroup';
import { Row } from 'primereact/row';
import { InputText } from 'primereact/inputtext';
import { Tag } from 'primereact/tag';
import { Card } from 'primereact/card';
import { ProgressSpinner } from 'primereact/progressspinner';
import moment from 'moment';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { selectSalesData, selectSummary, selectPaymentModes, selectLoading, selectFromDate, selectToDate, selectSelectedBranch, selectError } from '../../store/slices/salesReportaSlice';
import useUser from '../../hooks/useUser';
import ReportSummaryWithChart from './ReportSummaryWithChart';
import { exportStyledExcel, buildReportFilterLines } from '../../utilities/excelExport';
import './billreports.css';

const SalesReportaContent = ({ isManagerView = false }) => {
    const salesData = useSelector(selectSalesData);
    const summary = useSelector(selectSummary);
    const paymentModes = useSelector(selectPaymentModes);
    const loading = useSelector(selectLoading);
    const fromDate = useSelector(selectFromDate);
    const toDate = useSelector(selectToDate);
    const selectedBranch = useSelector(selectSelectedBranch);
    const error = useSelector(selectError);

    const { userRole } = useUser();
    const rolename = userRole || localStorage.getItem('role') || '';
    const isCEO = rolename.toUpperCase() === 'CEO';
    const isAdmin = rolename.toUpperCase() === 'ADMIN';
    const canExport = !isManagerView && (isCEO || isAdmin);

    const [filteredSalesData, setFilteredSalesData] = useState([]);
    const [globalFilter, setGlobalFilter] = useState('');

    const formatCurrency = (amount) => {
        const num = parseFloat(amount || 0);
        return num.toLocaleString('en-IN', { maximumFractionDigits: 2, minimumFractionDigits: 2 });
    };

    useEffect(() => {
        if (!globalFilter) {
            setFilteredSalesData(salesData);
            return;
        }
        const searchTerm = globalFilter.toLowerCase();
        const filtered = salesData.filter((item) => Object.values(item).some((value) => value !== null && value !== undefined && String(value).toLowerCase().includes(searchTerm)));
        setFilteredSalesData(filtered);
    }, [globalFilter, salesData]);

    const formatSummaryLabel = (key) => (key || '').replace(/^total_/i, '').replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());

    const getSummaryCardStyle = (key) => {
        const k = (key || '').toUpperCase();
        const styles = {
            CASH: { bg: 'linear-gradient(135deg, #1baf7a 0%, #0f766e 100%)', icon: 'pi-wallet' },
            CARD: { bg: 'linear-gradient(135deg, #2a78d6 0%, #184f95 100%)', icon: 'pi-credit-card' },
            PAYTM: { bg: 'linear-gradient(135deg, #eda100 0%, #c98500 100%)', icon: 'pi-mobile' },
            UPI: { bg: 'linear-gradient(135deg, #eda100 0%, #c98500 100%)', icon: 'pi-mobile' },
            PHONEPE: { bg: 'linear-gradient(135deg, #eda100 0%, #c98500 100%)', icon: 'pi-mobile' },
            CREDIT: { bg: 'linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)', icon: 'pi-clock' },
            TOTAL_TOTAL: { bg: 'linear-gradient(135deg, #ca8a04 0%, #854d0e 100%)', icon: 'pi-money-bill' }
        };
        const fallbackPalette = [
            { bg: 'linear-gradient(135deg, #0d9488 0%, #0f766e 100%)', icon: 'pi-wallet' },
            { bg: 'linear-gradient(135deg, #e87ba4 0%, #be3a72 100%)', icon: 'pi-mobile' },
            { bg: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)', icon: 'pi-dollar' }
        ];
        for (const [match, style] of Object.entries(styles)) {
            if (k.includes(match)) return { bg: style.bg, icon: style.icon, isHighlight: false };
        }
        const hash = (key || '').split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
        const fallback = fallbackPalette[Math.abs(hash) % fallbackPalette.length];
        return { bg: fallback.bg, icon: fallback.icon, isHighlight: false };
    };

    const excludeSalesTotalFromChart = (key) => (key || '').toUpperCase() === 'TOTAL_TOTAL';
    const emphasizeSalesTotal = (key) => (key || '').toUpperCase() === 'TOTAL_TOTAL';
    const alwaysShowSalesField = (key) => (key || '').toUpperCase() === 'TOTAL_TOTAL';

    const usedPaymentModes = paymentModes.filter((mode) => salesData.some((row) => Number(row[mode]) > 0));

    const getColumns = () => {
        const source = filteredSalesData.length > 0 ? filteredSalesData : salesData;
        if (source.length === 0) return [];
        const dateColumn = { field: 'invoice_date', header: 'Invoice Date', sortable: true, style: { minWidth: '120px', textAlign: 'left' } };
        const modeColumns = usedPaymentModes.map((mode) => ({
            field: mode,
            header: mode,
            sortable: true,
            body: (row) => `₹${formatCurrency(row[mode])}`,
            style: { minWidth: '120px', textAlign: 'right' }
        }));
        const totalColumn = { field: 'Total', header: 'Total', sortable: true, body: (row) => `₹${formatCurrency(row.Total)}`, style: { minWidth: '130px', textAlign: 'right', fontWeight: 600 } };
        return [dateColumn, ...modeColumns, totalColumn];
    };
    const columns = getColumns();

    const exportToExcel = async () => {
        if (filteredSalesData.length === 0) return;
        const columnDefs = [
            { header: 'Invoice Date', getValue: (r) => r.invoice_date || '-' },
            ...usedPaymentModes.map((mode) => ({ header: mode, getValue: (r) => Number(r[mode] || 0), numeric: true })),
            { header: 'Total', getValue: (r) => Number(r.Total || 0), numeric: true }
        ];
        await exportStyledExcel({
            fileName: `SalesReporta_${moment().format('YYYY-MM-DD')}.xlsx`,
            sheetName: 'Sales Reporta',
            title: 'Sales Reporta (Closed Bills)',
            filterLines: buildReportFilterLines({ fromDate, toDate, selectedBranch }),
            columns: columnDefs,
            rows: filteredSalesData
        });
    };

    const exportToPDF = () => {
        if (filteredSalesData.length === 0) return;
        const doc = new jsPDF();
        doc.setFontSize(16);
        doc.text('Sales Reporta (Closed Bills)', 14, 15);
        doc.setFontSize(10);
        let yPos = 25;
        if (fromDate && toDate) {
            doc.text(`Period: ${moment(fromDate).format('DD-MM-YYYY')} to ${moment(toDate).format('DD-MM-YYYY')}`, 14, yPos);
            yPos += 5;
        }
        if (selectedBranch) {
            doc.text(`Branch: ${selectedBranch.branch_name || selectedBranch}`, 14, yPos);
            yPos += 5;
        }
        const headers = ['Invoice Date', ...usedPaymentModes, 'Total'];
        const tableBody = filteredSalesData.map((row) => [row.invoice_date || '-', ...usedPaymentModes.map((mode) => `₹${formatCurrency(row[mode])}`), `₹${formatCurrency(row.Total)}`]);
        if (Object.keys(summary).length > 0) {
            tableBody.push(['TOTAL', ...usedPaymentModes.map((mode) => `₹${formatCurrency(summary[`Total_${mode}`] ?? 0)}`), `₹${formatCurrency(summary.Total_Total ?? 0)}`]);
        }
        autoTable(doc, {
            startY: yPos + 5,
            head: [headers],
            body: tableBody,
            styles: { fontSize: 8 },
            headStyles: { fillColor: [0, 122, 255] },
            footStyles: { fontStyle: 'bold', fillColor: [240, 240, 240] },
            didParseCell: (data) => {
                if (data.row.index === tableBody.length - 1 && Object.keys(summary).length > 0) {
                    data.cell.styles.fontStyle = 'bold';
                    data.cell.styles.fillColor = [240, 240, 240];
                }
            }
        });
        doc.save(`SalesReporta_${moment().format('YYYY-MM-DD')}.pdf`);
    };

    if (loading) {
        return (
            <div className="bill-reports-container">
                <Card>
                    <div className="flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
                        <ProgressSpinner />
                    </div>
                </Card>
            </div>
        );
    }

    return (
        <div className="bill-reports-container">
            {error && (
                <div className="mb-3 p-3 border-round" style={{ backgroundColor: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c' }}>
                    <span>{error}</span>
                </div>
            )}
            {!isManagerView && Object.keys(summary).length > 0 && (
                <ReportSummaryWithChart
                    summary={summary}
                    fromDate={fromDate}
                    toDate={toDate}
                    selectedBranch={selectedBranch}
                    title="Sales Reporta Summary"
                    subtitle="Closed-bill sales by final mode of payment"
                    getCardStyle={getSummaryCardStyle}
                    formatLabel={formatSummaryLabel}
                    isCurrency={() => true}
                    excludeFromChart={excludeSalesTotalFromChart}
                    emphasizeKey={emphasizeSalesTotal}
                    alwaysShowKey={alwaysShowSalesField}
                />
            )}

            <Card>
                <div className="flex flex-wrap justify-content-between align-items-center mb-4 gap-3">
                    <div className="flex align-items-center gap-2">
                        <h3 className="text-xl font-semibold m-0">Report Data</h3>
                        {filteredSalesData.length > 0 && <Tag value={`${filteredSalesData.length} Records`} severity="info" />}
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <span className="p-input-icon-left w-full sm:w-auto" style={{ minWidth: '250px' }}>
                            <i className="pi pi-search"></i>
                            <InputText value={globalFilter} onChange={(e) => setGlobalFilter(e.target.value)} placeholder="Search sales..." className="w-full" />
                        </span>
                        {canExport && (
                            <>
                                <Button label="Export Excel" icon="pi pi-file-excel" className="p-button-success" onClick={exportToExcel} disabled={filteredSalesData.length === 0} />
                                <Button label="Export PDF" icon="pi pi-file-pdf" className="p-button-danger" onClick={exportToPDF} disabled={filteredSalesData.length === 0} />
                            </>
                        )}
                    </div>
                </div>

                {filteredSalesData.length === 0 ? (
                    <div className="flex flex-column align-items-center justify-content-center p-6">
                        <i className="pi pi-inbox text-6xl text-color-secondary mb-3"></i>
                        <p className="text-xl text-color-secondary">No data available</p>
                        <p className="text-color-secondary mt-2">
                            {salesData.length === 0 ? (fromDate && toDate ? 'No closed sales found for the selected filters' : 'Please apply filters to view sales reporta') : 'No records match your search criteria'}
                        </p>
                    </div>
                ) : (
                    <DataTable
                        value={filteredSalesData}
                        showGridlines
                        paginator
                        rows={25}
                        stripedRows
                        rowsPerPageOptions={[10, 25, 50, 100]}
                        paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
                        currentPageReportTemplate="{first} to {last} of {totalRecords} records"
                        tableStyle={{ minWidth: '50rem' }}
                        className="p-datatable-sm"
                        emptyMessage="No sales data found"
                        footerColumnGroup={
                            !isManagerView && Object.keys(summary).length > 0 && columns.length > 0 ? (
                                <ColumnGroup>
                                    <Row>
                                        {columns.map((col, index) => (
                                            <Column
                                                key={col.field}
                                                footer={index === 0 ? 'Grand Total:' : `₹${formatCurrency(summary[`Total_${col.field}`] ?? 0)}`}
                                                style={{ textAlign: index === 0 ? 'left' : 'right', fontWeight: 'bold', fontSize: '1rem' }}
                                            />
                                        ))}
                                    </Row>
                                </ColumnGroup>
                            ) : null
                        }
                    >
                        {columns.map((col) => (
                            <Column key={col.field} field={col.field} header={col.header} sortable={col.sortable} body={col.body} style={col.style} />
                        ))}
                    </DataTable>
                )}
            </Card>
        </div>
    );
};

export default SalesReportaContent;
